import { useState, forwardRef } from "react";
import { motion } from "framer-motion";
import { Mail, Loader2, CheckCircle } from "lucide-react";
import { EcMark } from "@/components/icons/EcMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { subscribeNewsletter } from "@/lib/leads";

interface BlogNewsletterCTAProps {
  articleSlug?: string;
}

export const BlogNewsletterCTA = forwardRef<HTMLDivElement, BlogNewsletterCTAProps>(({ articleSlug }, ref) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { language } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);

    try {
      const { data, error } = await subscribeNewsletter({
        email,
        source: `blog_article_${articleSlug || "footer"}`,
      });

      if (error) throw error;

      if (data.already_subscribed) {
        toast.info(data.message);
      } else {
        toast.success(data.message);
      }
      setSuccess(true);
    } catch (error: any) {
      toast.error(error.message || "Failed to subscribe");
    } finally {
      setLoading(false);
    }
  };

  const t = {
    el: {
      title: "Σας άρεσε το άρθρο;",
      subtitle: "Ένα σύντομο email την εβδομάδα με πρακτικά CRO και UX tips",
      placeholder: "Το email σας",
      cta: "Εγγραφή",
      successTitle: "Τέλεια!",
      successMessage: "Το πρώτο email έρχεται μέσα στην εβδομάδα.",
    },
    en: {
      title: "Enjoyed this article?",
      subtitle: "One short email a week with practical CRO and UX tips",
      placeholder: "Your email",
      cta: "Subscribe",
      successTitle: "You are on the list",
      successMessage: "The first email arrives within the week.",
    }
  };

  const text = t[language] || t.en;

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="my-12 p-5 md:p-8 rounded-2xl bg-gradient-to-br from-primary to-[hsl(var(--ec-mint))] dark:from-primary/30 dark:to-[hsl(var(--ec-mint)/0.3)] border border-[hsl(var(--ec-mint)/0.3)] dark:border-[hsl(var(--ec-mint)/0.5)]"
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/50 flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-[hsl(var(--ec-mint))]" />
          </div>
          <h3 className="text-xl font-bold text-[hsl(var(--ec-mint))] dark:text-[hsl(var(--ec-mint))] mb-2">
            {text.successTitle}
          </h3>
          <p className="text-[hsl(var(--ec-mint))] dark:text-[hsl(var(--ec-mint))]">
            {text.successMessage}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="my-12 p-5 md:p-8 rounded-2xl bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 border border-primary/20 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/10 rounded-full blur-2xl" />

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <Mail className="w-5 h-5 text-primary" />
          </div>
          <EcMark className="w-4 h-4 text-accent animate-pulse" />
        </div>

        <h3 className="text-2xl font-bold mb-2">{text.title}</h3>
        <p className="text-muted-foreground mb-2">{text.subtitle}</p>
<div className="mb-6" />

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="email"
              placeholder={text.placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12"
              required
              disabled={loading}
            />
          </div>
          <Button 
            type="submit" 
            size="lg"
            className="h-12 px-8 font-semibold"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {text.cta}

              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground mt-4">
          {language === "el" 
            ? "Χωρίς spam. Μπορείτε να διαγραφείτε ανά πάσα στιγμή." 
            : "No spam. Unsubscribe anytime."}
        </p>
      </div>
    </motion.div>
  );
});

BlogNewsletterCTA.displayName = 'BlogNewsletterCTA';
